import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button, Input } from '../../components/ui';
import { signupSchema, getPasswordStrength } from '../../lib/validation';
import { getUserProfile } from '../../firebase/firestore';
import { cn } from '../../lib/utils';

export default function SignUp() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle, error: authError, authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
    },
  });

  const password = watch('password');
  const passwordStrength = getPasswordStrength(password);

  const onSubmit = async (data) => {
    try {
      await signUp(data.email, data.password, data.displayName);
      // Redirect to email verification page
      navigate('/auth/verify-email', { replace: true });
    } catch (err) {
      // Error is handled by AuthContext
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const user = await signInWithGoogle();

      // Skip verifications in emulator mode
      const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';

      // Google users have verified email, but still need phone verification
      if (!isEmulator) {
        const profile = await getUserProfile(user.uid);
        if (!profile?.phoneVerified) {
          navigate('/auth/verify-phone', { replace: true });
          return;
        }
      }

      // All verified - go to home
      navigate('/', { replace: true });
    } catch (err) {
      // Error is handled by AuthContext
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle={
        <>
          Already have an account?{' '}
          <Link
            to="/auth/login"
            className="font-medium text-primary hover:text-primary-hover"
          >
            Sign in
          </Link>
        </>
      }
    >
      <div className="space-y-6">
        {/* Auth error */}
        {authError && (
          <div className="rounded-md bg-error-light p-4">
            <p className="text-sm text-error">{authError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Display Name */}
          <Input
            label="Full name"
            type="text"
            autoComplete="name"
            placeholder="John Murphy"
            error={errors.displayName?.message}
            dark
            {...register('displayName')}
          />

          {/* Email */}
          <Input
            label="Email address"
            type="email"
            autoComplete="email"
            placeholder="john@example.com"
            error={errors.email?.message}
            dark
            {...register('email')}
          />

          {/* Password */}
          <div>
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              error={errors.password?.message}
              dark
              {...register('password')}
            />
            {/* Password strength indicator */}
            {password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={cn(
                        'h-1 flex-1 rounded-full transition-colors',
                        level <= passwordStrength.score
                          ? passwordStrength.color
                          : 'bg-midnight/30'
                      )}
                    />
                  ))}
                </div>
                <p className={cn(
                  'text-xs',
                  passwordStrength.score <= 1 && 'text-danger',
                  passwordStrength.score === 2 && 'text-warning',
                  passwordStrength.score === 3 && 'text-primary',
                  passwordStrength.score >= 4 && 'text-success',
                )}>
                  {passwordStrength.label}
                  {passwordStrength.score < 3 && ' — Use uppercase, lowercase, and numbers'}
                </p>
              </div>
            )}
          </div>

          {/* Terms */}
          <p className="text-xs text-gray-500">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:text-primary-hover">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-primary hover:text-primary-hover">
              Privacy Policy
            </Link>
            .
          </p>

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            loading={authLoading}
          >
            Create account
          </Button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-midnight/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-cream text-midnight/50">or</span>
          </div>
        </div>

        {/* Google Sign Up */}
        <Button
          type="button"
          variant="outline-dark"
          fullWidth
          onClick={handleGoogleSignUp}
          disabled={authLoading}
          iconLeft={<GoogleIcon />}
        >
          Continue with Google
        </Button>
      </div>
    </AuthLayout>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
